/**
 * Cliente da API.
 *
 * Caminho relativo, sempre: o front é servido pelo mesmo Express. Não há URL de
 * backend em lugar nenhum, e é isso que faz o cookie de sessão viajar — ele é
 * `__Host-`, preso a uma origem só.
 *
 * ## O 401 tem tratamento próprio
 *
 * A API responde 401 em JSON, nunca com redirecionamento. Se redirecionasse, o
 * `fetch` seguiria para a Microsoft e voltaria HTML no lugar do JSON — a
 * requisição responde 200 e quem estoura é o `JSON.parse`, longe da causa.
 *
 * Aqui o 401 vira `ErroDeSessao`, e quem chama decide: a tela do portfólio
 * manda para o login, uma ação de fundo pode só desistir em silêncio.
 */

export class ApiError extends Error {
  constructor(message, { status, code, detail } = {}) {
    super(message);
    this.name = 'ErroDaApi';
    this.status = status;
    this.code = code;
    // O que o servidor mandou junto com a recusa. O `errorHandler` só inclui
    // `detail` quando a rota o preenche, e é por onde chega o dado que a tela
    // precisa para oferecer um próximo passo em vez de só dizer que não deu —
    // por exemplo, quantas pessoas um vínculo de grupo liberaria.
    this.detail = detail;
  }
}

export class SessionError extends ApiError {
  constructor(message = 'Sua sessão expirou. Entre novamente.') {
    super(message, { status: 401, code: 'nao_autenticado' });
    this.name = 'ErroDeSessao';
  }
}

/**
 * @param {string} caminho  sempre começando com `/api`
 * @param {RequestInit} [opcoes]
 */
export async function apiFetch(path, options = {}) {
  let res;
  try {
    res = await fetch(path, {
      ...options,
      headers: {
        accept: 'application/json',
        // ## Corpo de texto ganha `content-type` sozinho
        //
        // Sem ele o `express.json()` **não analisa o corpo** — e não é erro:
        // o analisador simplesmente não casa, `req.body` chega vazio, e a rota
        // responde 400 por campo obrigatório ausente. O corpo foi enviado
        // inteiro e perfeito; ninguém o leu.
        //
        // Mordeu em 22/10: o tour guiado gravava progresso por aqui, sem
        // cabeçalho, e **toda** gravação respondia 400. O convite do primeiro
        // acesso voltaria para sempre, e "não me ofereça mais" nunca pegaria.
        //
        // Só para corpo de TEXTO: envio de arquivo cru define o próprio tipo
        // (`application/octet-stream`, `image/png`), e sobrescrevê-lo aqui
        // quebraria o upload de arquivo e de logotipo.
        ...(typeof options.body === 'string' ? { 'content-type': 'application/json' } : {}),
        ...(options.headers ?? {}),
      },
    });
  } catch {
    // Rede caiu, servidor não respondeu, aba offline. Mensagem separada de
    // propósito: "verifique sua conexão" é acionável, "erro 0" não é.
    throw new ApiError('Não foi possível falar com o servidor. Verifique sua conexão.', {
      status: 0,
      code: 'sem_rede',
    });
  }

  if (res.status === 401) throw new SessionError();

  // 204 e afins não têm corpo; tentar decodificar estouraria.
  const hasBody = res.status !== 204 && res.headers.get('content-length') !== '0';
  let body = null;
  if (hasBody) {
    try {
      body = await res.json();
    } catch {
      body = null;
    }
  }

  if (!res.ok) {
    throw new ApiError(body?.error?.message ?? 'Algo deu errado. Tente novamente.', {
      status: res.status,
      code: body?.error?.code,
      detail: body?.error?.detail,
    });
  }

  return body;
}

/** Manda para o login guardando onde a pessoa estava. */
export function goToSignIn() {
  const target = window.location.pathname + window.location.search;
  window.location.assign(`/entrar?returnTo=${encodeURIComponent(target)}`);
}

export const api = {
  me: () => apiFetch('/api/me'),
  catalog: () => apiFetch('/api/catalog'),
  item: (id) => apiFetch(`/api/catalog/${encodeURIComponent(id)}`),

  notifications: () => apiFetch('/api/notifications'),

  /** Dúvidas e sugestões: as minhas, com a conversa inteira em `messages`. */
  feedback: () => apiFetch('/api/feedback'),

  sendFeedback: (body) =>
    apiFetch('/api/feedback', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    }),

  /** Responde dentro do próprio assunto. Reabre a conversa se estava encerrada. */
  replyFeedback: (id, message) =>
    apiFetch(`/api/feedback/${encodeURIComponent(id)}/mensagens`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ message }),
    }),

  /** Sem `id`, marca todas. Devolve a lista já atualizada. */
  markNotificationsRead: (id = null) =>
    apiFetch('/api/notifications/read', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(id ? { id } : {}),
    }),

  /**
   * Páginas hospedadas.
   *
   * Projeto de outra pessoa responde **404**, e não 403: confirmar que ele
   * existe já é informação, porque num projeto público o endereço é a
   * credencial. A tela trata as duas situações do mesmo jeito — "não
   * encontrado" — e é a resposta honesta para quem digitou um id na barra.
   */
  sites: {
    list: () => apiFetch('/api/sites'),
    get: (id) => apiFetch(`/api/sites/${encodeURIComponent(id)}`),

    create: (body) =>
      apiFetch('/api/sites', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
      }),

    save: (id, changes) =>
      apiFetch(`/api/sites/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(changes),
      }),

    remove: (id) => apiFetch(`/api/sites/${encodeURIComponent(id)}`, { method: 'DELETE' }),

    /**
     * Troca o endereço.
     *
     * **Sem chamador desde 21/10**, e de propósito. A tela que a usava saiu
     * junto com o alcance `PUBLICO`, onde o endereço ERA a credencial — com
     * compartilhamento por pessoa, tirar alguém da lista corta o acesso tendo
     * ou não o link. Ver o comentário em `SiteDetail.jsx`.
     *
     * Fica aqui porque a rota existe e continua correta: trocar o slug é o
     * único jeito de invalidar os crachás de conteúdo em circulação, já que o
     * caminho os contém. Se alguém pedir o botão de volta, é só isto.
     */
    rotateAddress: (id) =>
      apiFetch(`/api/sites/${encodeURIComponent(id)}/endereco`, { method: 'POST' }),

    /**
     * Envia um arquivo, cru, um por requisição.
     *
     * ## Por que o `content-type` vai como `application/octet-stream`
     *
     * O upload de logotipo manda `file.type`, porque lá o servidor confia nele.
     * Aqui **o tipo sai da extensão do caminho** e o cabeçalho não decide nada —
     * mandar `file.type` daria a impressão errada de que ele importa.
     *
     * E há um caso em que mandar o tipo real **atrapalharia**: um arquivo
     * `.json` chega com `file.type === 'application/json'`, e o `express.json`
     * do servidor tem exceção justamente para isso. A exceção existe e é
     * testada, mas não há razão de pisar nela de propósito.
     *
     * ## O caminho vai codificado segmento por segmento
     *
     * `encodeURIComponent` no caminho inteiro transformaria as barras em `%2F`,
     * e a rota deixaria de casar. Por segmento, acento e espaço viajam
     * codificados e as barras continuam sendo barras.
     */
    uploadFile: (id, caminho, file) =>
      apiFetch(
        `/api/sites/${encodeURIComponent(id)}/arquivos/${caminho
          .split('/')
          .map(encodeURIComponent)
          .join('/')}`,
        {
          method: 'PUT',
          headers: { 'content-type': 'application/octet-stream' },
          body: file,
        },
      ),

    deleteFile: (id, caminho) =>
      apiFetch(
        `/api/sites/${encodeURIComponent(id)}/arquivos/${caminho
          .split('/')
          .map(encodeURIComponent)
          .join('/')}`,
        { method: 'DELETE' },
      ),

    /* ── compartilhamento (21/10) ──────────────────────────────────────── */

    shares: (id) => apiFetch(`/api/sites/${encodeURIComponent(id)}/compartilhamento`),

    share: (id, upn) =>
      apiFetch(`/api/sites/${encodeURIComponent(id)}/compartilhamento`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ upn }),
      }),

    // O e-mail vai no caminho e precisa de `encodeURIComponent`: ele tem `@` e
    // pontos, e um endereço malformado com `/` partiria a rota sem escapar.
    unshare: (id, upn) =>
      apiFetch(`/api/sites/${encodeURIComponent(id)}/compartilhamento/${encodeURIComponent(upn)}`, {
        method: 'DELETE',
      }),
  },

  /** Quem já entrou no AI Hub, para o seletor de compartilhamento. */
  usuarios: (q = '') => apiFetch(`/api/usuarios${q ? `?q=${encodeURIComponent(q)}` : ''}`),

  /**
   * Conector do Claude.
   *
   * O token em claro volta **uma única vez**, na resposta do `create`. O
   * servidor guarda só o hash — nem ele consegue mostrá-lo de novo. A tela
   * precisa exibi-lo nesse instante ou ele se perde.
   */
  conector: {
    get: () => apiFetch('/api/conector'),

    createToken: (name) =>
      apiFetch('/api/conector/tokens', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ name }),
      }),

    revokeToken: (id) =>
      apiFetch(`/api/conector/tokens/${encodeURIComponent(id)}`, { method: 'DELETE' }),
  },

  /**
   * Painel de administração.
   *
   * Responde 403 a quem não é administrador — e o front NÃO esconde a rota por
   * isso. Esconder seria segurança por obscuridade: o pacote é o mesmo para
   * todo mundo e já traz a tela. O que protege é o servidor recusar; a tela
   * apenas explica a recusa.
   */
  admin: {
    users: () => apiFetch('/api/admin/users'),
    setAdmin: (oid, isAdmin) =>
      apiFetch(`/api/admin/users/${encodeURIComponent(oid)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ isAdmin }),
      }),
    catalog: () => apiFetch('/api/admin/catalog'),
    createItem: (item) =>
      apiFetch('/api/admin/catalog', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(item),
      }),

    saveItem: (id, changes) =>
      apiFetch(`/api/admin/catalog/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(changes),
      }),

    /**
     * O arquivo vai como corpo cru, sem `multipart`.
     *
     * O `Content-Type` sai do próprio `File` — por isso não se define header
     * aqui: definir um sobrescreveria o tipo real e o servidor recusaria a
     * imagem por não reconhecer o formato.
     */
    uploadLogo: (id, file) =>
      apiFetch(`/api/admin/catalog/${encodeURIComponent(id)}/logo`, {
        method: 'PUT',
        headers: { 'content-type': file.type },
        body: file,
      }),

    deleteLogo: (id) =>
      apiFetch(`/api/admin/catalog/${encodeURIComponent(id)}/logo`, { method: 'DELETE' }),

    /**
     * Sessões de conversa. Filtro vazio é omitido da query — mandar
     * `?itemId=` faria o servidor filtrar por string vazia e devolver nada.
     */
    conversations: (filters = {}) => {
      const q = new URLSearchParams();
      for (const [k, v] of Object.entries(filters)) {
        if (v !== null && v !== undefined && v !== '' && v !== false) q.set(k, String(v));
      }
      const qs = q.toString();
      return apiFetch(`/api/admin/conversations${qs ? `?${qs}` : ''}`);
    },

    conversation: (id) => apiFetch(`/api/admin/conversations/${encodeURIComponent(id)}`),

    bindings: () => apiFetch('/api/admin/bindings'),

    access: () => apiFetch('/api/admin/access'),

    requests: (status) =>
      apiFetch(`/api/admin/requests${status ? `?status=${encodeURIComponent(status)}` : ''}`),

    /**
     * Decide uma solicitação pelo painel.
     *
     * A decisão é gravada **no ServiceNow**, não aqui — quem concede o acesso
     * continua sendo o acompanhamento do worker, na passada seguinte. Por isso
     * a resposta traz uma mensagem falando em "até 1 minuto": a tela não deve
     * prometer acesso liberado no instante do clique.
     */
    decideRequest: (id, decision, comment) =>
      apiFetch(`/api/admin/requests/${encodeURIComponent(id)}/decision`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ decision, ...(comment ? { comment } : {}) }),
      }),

    feedback: () => apiFetch('/api/admin/feedback'),

    /** Acrescenta a resposta da equipe. Nunca reescreve o que já foi dito. */
    answerFeedback: (id, answer) =>
      apiFetch(`/api/admin/feedback/${encodeURIComponent(id)}`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ answer }),
      }),

    /** Encerra ou devolve para a fila. Rota separada porque não escreve conteúdo. */
    setFeedbackStatus: (id, status) =>
      apiFetch(`/api/admin/feedback/${encodeURIComponent(id)}/estado`, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ status }),
      }),

    grantAccess: (userOid, itemId) =>
      apiFetch('/api/admin/access', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ userOid, itemId }),
      }),

    revokeAccess: (userOid, itemId) =>
      apiFetch(`/api/admin/access/${encodeURIComponent(userOid)}/${encodeURIComponent(itemId)}`, {
        method: 'DELETE',
      }),

    bind: (source, externalId, itemId) =>
      apiFetch(
        `/api/admin/bindings/${encodeURIComponent(source)}/${encodeURIComponent(externalId)}`,
        {
          method: 'PATCH',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ itemId }),
        },
      ),
  },
};

/**
 * Endereço das imagens. Todas devolvem 204 quando não há.
 *
 * `me` leva o `oid` na URL **de propósito**, e não por acaso: sem ele a rota é
 * a mesma para todo mundo, e o navegador serve a foto em cache da conta
 * anterior quando alguém troca de usuário. Aconteceu em 01/09. O `oid` não é
 * segredo — já vem em `/api/me`.
 *
 * O logotipo leva `v=`, com a data do último envio: é o que faz o cache de um
 * dia conviver com a troca da imagem pelo painel.
 */
export const photoUrl = {
  me: (oid) => `/api/me/photo${oid ? `?u=${encodeURIComponent(oid)}` : ''}`,

  /** Área administrativa. Responde 403 para quem não é administrador. */
  user: (oid) => `/api/admin/users/${encodeURIComponent(oid)}/photo`,

  /**
   * A mesma foto, para qualquer pessoa autenticada.
   *
   * Existe separada de `user` porque aquela é da área administrativa: usá-la
   * no seletor de compartilhamento faria os avatares aparecerem **só para
   * administradores** — e quem constrói a tela costuma ser administrador, o
   * que faz o defeito passar despercebido.
   */
  usuario: (oid) => `/api/usuarios/${encodeURIComponent(oid)}/foto`,
};

export const logoUrl = (id, version) =>
  `/api/catalog/${encodeURIComponent(id)}/logo${version ? `?v=${encodeURIComponent(version)}` : ''}`;

export default api;
